import { FC, FunctionComponent, useState } from 'react';
import SubscriptionsRedemptionsTable from './subscriptionsRedemptionsTable.component';
import SubscriptionsRedemptionsChart from './subcsriptionsRedemptionsChart.component';
import { Button } from '@mui/material';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import UltraTable, { TableData } from '../../../tables/ultraTable/UltraTable';
import { formatDateForCheckingState } from '../../../../utilities/dateFormatters';
import {
  ALL_SUB_RED_COLS,
  SubscriptionRedemptionData,
  buildColumns,
  buildData,
} from './utils/subscription-redemption-utils';
import { EditableFields } from '../../../tables/ultraTable/types/editable.types';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { useSubscriptionsRedemptionsPageModel } from '../../../../api/rs-hg/liquidity/hooks/queries';
import {
  BottomPanelView,
  SubscriptionsRedemptionsReport,
  TopPanelView,
} from './subscriptionsRedemptions.types';
import {
  BOTTOM_PANEL_VIEWS,
  TOP_PANEL_VIEWS,
} from './utils/subRedPanelViews';

const SubscriptionsRedemptions: FC<FundInfoComponentProps> = (props) => {
  const { positionDate: activeDate, fundId } = props;
  const isPositionDateLoading = !!fundId && !activeDate;

  const {
    data: subscriptionsRedemptionsPage,
    error,
    isError,
    isLoading,
    isFetching,
  } = useSubscriptionsRedemptionsPageModel(fundId, activeDate);

  return (
    <QueryBoundary<SubscriptionsRedemptionsReport>
      isLoading={isPositionDateLoading || isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      data={subscriptionsRedemptionsPage?.data}
      loadingMessages={
        isPositionDateLoading
          ? ['Loading position date...']
          : ['Loading Subscriptions/Redemptions Data...']
      }
      errorMessage="No Data Available."
      noDataMessage="No Data Available."
    >
      {(resolvedData) => (
        <SubscriptionsRedemptionsContent
          data={resolvedData}
          fundId={fundId}
        />
      )}
    </QueryBoundary>
  );
};

const SubscriptionsRedemptionsContent: FC<{
  data: SubscriptionsRedemptionsReport;
  fundId: string;
}> = ({ data, fundId }) => {
  const [topView, setTopView] = useState<TopPanelView>('redemption');
  const [bottomView, setBottomView] = useState<BottomPanelView>('gross');
  const absSubsRedsData = data.abs_subs_reds_data;
  const positionDate = data.selected_position_date;

  return (
    <>
      <SubscriptionsRedemptionsTable
        data={data}
        view={topView}
        views={TOP_PANEL_VIEWS}
        onViewChange={setTopView}
      />
      <SubscriptionsRedemptionsChart
        data={data}
        view={topView}
        views={TOP_PANEL_VIEWS}
        onViewChange={setTopView}
      />
      <SubscriptionsRedemptionsTable
        data={data}
        view={bottomView}
        views={BOTTOM_PANEL_VIEWS}
        onViewChange={setBottomView}
      />
      <SubscriptionsRedemptionsChart
        data={data}
        view={bottomView}
        views={BOTTOM_PANEL_VIEWS}
        onViewChange={setBottomView}
      />

      {absSubsRedsData && (
        <ButlerTableErrorBoundary
          fundId={fundId}
          positionDate={positionDate || ''}
          absSubsRedsData={absSubsRedsData}
        />
      )}
    </>
  );
};

export default SubscriptionsRedemptions;

export interface SubscriptionRedemptionDataEditable
  extends SubscriptionRedemptionData,
  EditableFields { }

interface ButlerTableErrorBoundaryProps {
  absSubsRedsData: SubscriptionRedemptionData[];
  positionDate: string;
  fundId: string;
}

const ButlerTableErrorBoundary: FunctionComponent<
  ButlerTableErrorBoundaryProps
> = ({ absSubsRedsData, positionDate, fundId }) => {
  const [viewType, setViewType] = useState<
    'SUBSCRIPTION' | 'REDEMPTION' | null
  >(null);

  const data = absSubsRedsData.filter(
    (row: SubscriptionRedemptionData) =>
      viewType === null || row.type === viewType,
  );

  const tableData: TableData<any> = {
    columns: buildColumns(ALL_SUB_RED_COLS),
    data: buildData(data),
  };

  return (
    <UltraTable<SubscriptionRedemptionDataEditable>
      title="Subscriptions/Redemptions"
      tableData={tableData}
      getRowCanExpand={() => false}
      pdfConfig={{
        pdfIdentifier: `${fundId}_subscriptions_redemptions`,
        pdfTitle: `Subscriptions Redemptions Data - ${fundId}`,
        pdfExportGroupName: 'subscriptions_redemptions_page',
        pdfExportGroupOrder: 5,
        pdfFileName: `subscriptions-redemptions-${fundId}-${positionDate || formatDateForCheckingState(new Date())
          }`,
      }}
      actionBtns={[
        <Button
          key="subs"
          onClick={() =>
            setViewType((prev) =>
              prev === 'SUBSCRIPTION' ? null : 'SUBSCRIPTION',
            )
          }
          variant={viewType === 'SUBSCRIPTION' ? 'contained' : 'outlined'}
        >
          Subscriptions
        </Button>,
        <Button
          key="reds"
          onClick={() =>
            setViewType((prev) => (prev === 'REDEMPTION' ? null : 'REDEMPTION'))
          }
          variant={viewType === 'REDEMPTION' ? 'contained' : 'outlined'}
        >
          Redemptions
        </Button>,
      ]}
    />
  );
};
